import { Card, CardContent } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import {
  ORDER_STATUS_LABEL,
  ORDER_STATUSES_FOR_ADMIN,
} from "@shared/orderStatus";
import { formatKRW } from "@shared/pricing";
import { Image, Loader2, Package, Truck, Users, CreditCard, Sparkles, BarChart3, RefreshCw, Palette } from "lucide-react";
import { Link } from "wouter";
import { AdminLayout } from "./AdminLayout";

const STATUS_COLORS: Record<string, string> = {
  paid: "bg-blue-100 text-blue-800 border-blue-200",
  reviewing: "bg-yellow-100 text-yellow-800 border-yellow-200",
  producing: "bg-orange-100 text-orange-800 border-orange-200",
  ready_to_ship: "bg-purple-100 text-purple-800 border-purple-200",
  shipped: "bg-indigo-100 text-indigo-800 border-indigo-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const STYLE_LABELS: Record<string, string> = {
  seongsu: "성수동 감성", newyork: "뉴욕 갤러리", paris: "파리 아틀리에",
  nordic: "북유럽", jazzbar: "재즈바", cosmic: "우주",
  minimal: "미니멀", luxury: "럭셔리", movie_poster: "영화 포스터",
  ai_recommend: "AI 추천", none: "미지정",
};

export default function AdminDashboardPage() {
  const dashboard = trpc.admin.dashboard.useQuery();
  const aiAnalytics = trpc.admin.aiAnalytics.useQuery();

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-primary">대시보드</h1>
          <p className="text-sm text-muted-foreground">
            전체 운영 현황 한눈에 보기
          </p>
        </div>
      </div>

      {dashboard.isLoading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      ) : !dashboard.data ? null : (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="회원 수" value={dashboard.data.counts.users.toString()} icon={<Users className="w-4 h-4" />} color="text-blue-600" />
            <KPI label="총 주문" value={dashboard.data.counts.orders.toString()} icon={<Package className="w-4 h-4" />} color="text-orange-600" />
            <KPI label="결제 건수" value={dashboard.data.counts.payments.toString()} icon={<CreditCard className="w-4 h-4" />} color="text-green-600" />
            <KPI label="누적 매출" value={formatKRW(dashboard.data.revenue)} icon={<CreditCard className="w-4 h-4" />} color="text-primary" />
          </div>

          {/* AI Analytics Section */}
          {aiAnalytics.data && (
            <>
              <div className="font-serif text-lg text-primary flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                AI Art Director 분석
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KPI label="AI 세션" value={aiAnalytics.data.totalSessions.toString()} icon={<BarChart3 className="w-4 h-4" />} color="text-purple-600" />
                <KPI label="생성 이미지" value={aiAnalytics.data.totalImages.toString()} icon={<Image className="w-4 h-4" />} color="text-pink-600" />
                <KPI label="평균 품질" value={aiAnalytics.data.avgQuality ? `${aiAnalytics.data.avgQuality}점` : "-"} icon={<Sparkles className="w-4 h-4" />} color="text-amber-600" />
                <KPI label="자동 재시도" value={aiAnalytics.data.totalRetries.toString()} icon={<RefreshCw className="w-4 h-4" />} color="text-red-600" />
              </div>

              {/* Generate vs Edit ratio */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card>
                  <CardContent className="p-5">
                    <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      생성 / 수정 비율
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span>새 생성</span>
                          <span className="font-medium">{aiAnalytics.data.generateCount}건</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{
                              width: aiAnalytics.data.totalSessions > 0
                                ? `${(aiAnalytics.data.generateCount / aiAnalytics.data.totalSessions) * 100}%`
                                : "0%",
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span>수정</span>
                          <span className="font-medium">{aiAnalytics.data.editCount}건</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full transition-all"
                            style={{
                              width: aiAnalytics.data.totalSessions > 0
                                ? `${(aiAnalytics.data.editCount / aiAnalytics.data.totalSessions) * 100}%`
                                : "0%",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Style Popularity */}
                <Card>
                  <CardContent className="p-5">
                    <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      인기 스타일
                    </div>
                    <div className="space-y-2">
                      {Object.entries(aiAnalytics.data.styleCounts)
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .slice(0, 5)
                        .map(([style, count]) => (
                          <div key={style} className="flex items-center justify-between text-xs">
                            <span>{STYLE_LABELS[style] ?? style}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary/60 rounded-full"
                                  style={{
                                    width: `${((count as number) / aiAnalytics.data!.totalSessions) * 100}%`,
                                  }}
                                />
                              </div>
                              <span className="font-medium w-6 text-right">{count as number}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Order Status Pipeline */}
          <Card>
            <CardContent className="p-5">
              <div className="font-serif text-lg text-primary mb-4">
                주문 파이프라인
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                {ORDER_STATUSES_FOR_ADMIN.map((s) => {
                  const count = dashboard.data!.ordersByStatus[s] ?? 0;
                  return (
                    <Link
                      key={s}
                      href="/admin/orders"
                      className={`border rounded-lg px-3 py-3 text-center transition-all hover:shadow-sm ${STATUS_COLORS[s] ?? "bg-gray-50 border-gray-200"}`}
                    >
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-[10px] font-medium mt-0.5 leading-tight">
                        {ORDER_STATUS_LABEL[s]}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card>
            <CardContent className="p-5">
              <div className="flex justify-between mb-4">
                <div className="font-serif text-lg text-primary">
                  최근 주문
                </div>
                <Link
                  href="/admin/orders"
                  className="text-sm text-primary hover:underline"
                >
                  전체 보기 →
                </Link>
              </div>
              {dashboard.data.recentOrders.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  아직 주문이 없습니다.
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Table Header */}
                  <div className="hidden md:grid md:grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border/60">
                    <div className="col-span-1">이미지</div>
                    <div className="col-span-2">주문번호</div>
                    <div className="col-span-2">상태</div>
                    <div className="col-span-2">결제</div>
                    <div className="col-span-2">배송</div>
                    <div className="col-span-1">금액</div>
                    <div className="col-span-2">일시</div>
                  </div>

                  {dashboard.data.recentOrders.map((o) => (
                    <Link
                      key={o.id}
                      href={`/admin/orders/${o.id}`}
                      className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm hover:bg-muted/40 rounded-lg transition-colors"
                    >
                      <div className="col-span-1">
                        {o.imageUrl ? (
                          <img src={o.imageUrl} alt="" className="w-9 h-9 object-cover rounded" />
                        ) : (
                          <div className="w-9 h-9 rounded bg-muted flex items-center justify-center">
                            <Image className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="col-span-2 font-mono text-xs">{o.orderNo}</div>
                      <div className="col-span-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] ?? "bg-gray-100 text-gray-700"}`}>
                          {ORDER_STATUS_LABEL[o.status]}
                        </span>
                      </div>
                      <div className="col-span-2 text-xs text-green-600 font-medium">결제완료</div>
                      <div className="col-span-2 text-xs text-muted-foreground">
                        {(o as any).trackingNumber ? (
                          <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{(o as any).trackingNumber}</span>
                        ) : "-"}
                      </div>
                      <div className="col-span-1 font-medium">{formatKRW(o.totalAmount)}</div>
                      <div className="col-span-2 text-xs text-muted-foreground">
                        {new Date(o.createdAt).toLocaleDateString("ko-KR")}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}

function KPI({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <Card>
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <span className={color}>{icon}</span>
          {label}
        </div>
        <div className="font-serif text-xl md:text-2xl text-primary">{value}</div>
      </CardContent>
    </Card>
  );
}
